<template>
  <CollapseTitle
    class="collapse-title"
    :class="[
      `collapse-title--color-${ticketType.color}`,
      { 'collapse-title--disabled': disabled }
    ]"
    :disabled="disabled"
  >
    <div class="collapse-title__heading">
      <span
        class="collapse-title__title t-h1"
        v-html="nbsp(ticketType.title)"
      />
      <span
        v-if="status"
        class="collapse-title__status t-caption-small"
        v-html="status"
      />
    </div>
    <div
      class="collapse-title__description t-body"
      v-html="nbsp(ticketType.description)"
    />
    <ChevronIcon
      class="icon collapse-title__icon"
      aria-hidden="true"
      role="img"
    />
  </CollapseTitle>
</template>

<script setup lang="ts">
import ChevronIcon from '@/assets/icons/arrow-down.svg?component'
import { PublicTicketType, StatusEnum } from '@/service/__generated-api'

const props = defineProps<{ ticketType: PublicTicketType }>()
const { t } = useI18n()

const disabled = computed(() => props.ticketType.status === 'sold_out')

const status = computed(() => {
  const statusesMap: Record<StatusEnum, string> = {
    on_sale: t('ticket-status.on-sale'),
    sold_out: t('ticket-status.sold-out'),
    empty: ''
  }

  return props.ticketType.status ? statusesMap[props.ticketType.status] : ''
})
</script>

<style scoped lang="scss">
.collapse-title {
  @include focus-visible;
  display: grid;
  grid-template-columns: 10fr auto;
  gap: rem(28px) rem(8px);
  align-items: center;
  padding-block: rem(33px) rem(22px);
  padding-inline: rem(20px);

  @include media-up(md) {
    row-gap: rem(46px);
    padding-block: rem(48px);
    padding-inline: rem(32px);
  }

  @include media-up(xl) {
    grid-template-columns: 4fr 8fr auto;
    gap: 0;
    padding-block: rem(61px);
    padding-inline: rem(55px) rem(75px);
  }

  &--disabled {
    pointer-events: none;
    cursor: default;
    opacity: 0.4;
  }

  @each $key, $value in $places-colors {
    &--#{'color-' + $key} {
      background-color: $value;
    }
  }

  &__heading {
    grid-column: 1 / 2;

    @include media-up(xl) {
      padding-inline-end: rem(12px);
    }
  }

  &__status {
    margin-left: rem(8px);
    white-space: nowrap;
    vertical-align: top;
  }

  &__description {
    grid-column: 1 / 3;

    @include media-up(xl) {
      grid-column: 2 / 3;
      margin-inline: 0 calc(69 / 1440 * 100svw);
    }
  }

  &__icon {
    @include size(rem(23px));
    grid-row: 1;
    grid-column: 2 / 3;
    object-fit: contain;
    object-position: center;
    transition: transform $transition;

    @include media-up(xl) {
      @include size(rem(28px));
      grid-column: 3 / 4;
    }

    .collapse-title[aria-expanded='true'] & {
      transform: rotateX(180deg);
    }
  }
}
</style>
